// CascadeScoreHistoryChart — SVG sparkline rendering the last N predictor
// scores from localStorage as a colour-banded line, with horizontal threshold
// guides at 20 / 56 / 76 (MODERADO / ALTO / INMINENTE regimes from the same
// gating logic used by the banner).
//
// Mounted at the top of the Cascade History drawer (LiqMapPage.jsx).
//
// Props:
//   entries: array of { timestamp:number, score:number, nivel:string }, sorted
//            newest-first (the same shape persisted by useCascadePredictor).
//
// Behaviour:
//   - Reverses entries so the x-axis flows oldest → newest (left → right).
//   - Hides itself when there are fewer than 2 points (nothing to draw).
//   - Each dot is coloured by `nivel` to match the banner's colour scheme.
import React, { useMemo } from "react";

const W = 248;
const H = 80;
const PAD_X = 8;
const PAD_Y = 6;
const BAND_INMINENTE = 76;
const BAND_ALTO = 56;
const BAND_MODERADO = 20;

const colorFor = (nivel) => {
  if (nivel === "INMINENTE") return "#ff3b5c";
  if (nivel === "ALTO") return "#ff5a00";
  if (nivel === "MODERADO") return "#f0c800";
  return "#6b7280"; // sub-threshold: muted grey
};

export const CascadeScoreHistoryChart = ({ entries }) => {
  const points = useMemo(() => {
    if (!entries || entries.length < 2) return [];
    const ordered = [...entries].reverse(); // oldest → newest
    const innerW = W - PAD_X * 2;
    const innerH = H - PAD_Y * 2;
    return ordered.map((e, i) => {
      const xRatio = ordered.length === 1 ? 0 : i / (ordered.length - 1);
      const yRatio = Math.min(1, Math.max(0, (e.score || 0) / 100));
      return {
        x: PAD_X + xRatio * innerW,
        y: PAD_Y + (1 - yRatio) * innerH,
        score: e.score || 0,
        nivel: e.nivel,
        timestamp: e.timestamp,
      };
    });
  }, [entries]);

  if (points.length < 2) return null;

  const yFor = (score) => PAD_Y + (1 - score / 100) * (H - PAD_Y * 2);
  const pathD = points.map((p, i) => (i === 0 ? "M" : "L") + p.x.toFixed(1) + " " + p.y.toFixed(1)).join(" ");
  const minScore = Math.min(...points.map((p) => p.score));
  const maxScore = Math.max(...points.map((p) => p.score));

  return (
    <div
      data-testid="cascade-score-history-chart"
      className="mb-3 rounded border p-2"
      style={{ borderColor: "rgba(249,115,22,0.15)", background: "rgba(255,255,255,0.02)" }}
    >
      <div className="mb-1 flex items-center justify-between font-mono text-[9px] uppercase tracking-wider" style={{ color: "rgba(255,255,255,0.45)" }}>
        <span>Score · últimos {points.length}</span>
        <span style={{ color: "rgba(255,255,255,0.7)" }}>
          min <span style={{ color: colorFor(points[points.findIndex((p) => p.score === minScore)]?.nivel) }}>{minScore}</span>
          <span className="mx-1 opacity-30">·</span>
          max <span style={{ color: colorFor(points[points.findIndex((p) => p.score === maxScore)]?.nivel) }}>{maxScore}</span>
        </span>
      </div>
      <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`} className="block">
        {/* Regime bands as horizontal dashed guides */}
        <line x1={PAD_X} x2={W - PAD_X} y1={yFor(BAND_INMINENTE)} y2={yFor(BAND_INMINENTE)}
              stroke="#ff3b5c" strokeOpacity="0.25" strokeDasharray="2 3" strokeWidth="1" />
        <line x1={PAD_X} x2={W - PAD_X} y1={yFor(BAND_ALTO)} y2={yFor(BAND_ALTO)}
              stroke="#ff5a00" strokeOpacity="0.22" strokeDasharray="2 3" strokeWidth="1" />
        <line x1={PAD_X} x2={W - PAD_X} y1={yFor(BAND_MODERADO)} y2={yFor(BAND_MODERADO)}
              stroke="#f0c800" strokeOpacity="0.2" strokeDasharray="2 3" strokeWidth="1" />
        {/* Connecting line */}
        <path d={pathD} fill="none" stroke="#f97316" strokeOpacity="0.85" strokeWidth="1.5" strokeLinejoin="round" strokeLinecap="round" />
        {/* Dots */}
        {points.map((p, i) => (
          <circle
            key={`${p.timestamp}-${i}`}
            cx={p.x}
            cy={p.y}
            r={i === points.length - 1 ? 3 : 2}
            fill={colorFor(p.nivel)}
            stroke="#080508"
            strokeWidth="1"
          >
            <title>{`Score ${p.score} · ${p.nivel || "—"}`}</title>
          </circle>
        ))}
      </svg>
    </div>
  );
};

export default CascadeScoreHistoryChart;
